import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, Renderer2, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { ListsService } from '../../services/lists.service';
import { CommonModule } from '@angular/common';
import { signal } from '@angular/core';

@Component({
  selector: 'app-companies',
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.scss']
})
export class CompaniesComponent implements OnInit, AfterViewInit, OnDestroy {

  elements: any[] = [];
  filtered: any[] = [];
  form: FormGroup;
  
  // Signals para control de timeout
  offTimeOut = signal(false);
  hasWritten = signal(false);
  hasSelected = signal(false);
  private countdownTimer: any;
  private viewInitialized = signal(false);

  @Input() defaultValue: any = null;
  @Output() valueState = new EventEmitter<number | null>();
  @ViewChild('element') element!: ElementRef;
  @ViewChild('subtitleLabel') subtitleLabel!: ElementRef;
  @ViewChild('inbox') inbox!: ElementRef;
  
  isValid = false;

  constructor(
    private listService: ListsService,
    private renderer: Renderer2
  ) {
    this.form = new FormGroup({
      element: new FormControl('', [Validators.required]),
    });

    this.form.valueChanges.subscribe((value) => {
      this.hasWritten.set(true);
      
      if (this.form.valid && value.element && this.hasSelected()) {
        this.clearCountdown();
        this.offTimeOut.set(false);
        this.getStyleEnabled();
      }
    });
  }
  

  ngOnInit(): void {
    this.listService.getCompanies()
    .subscribe({
      next: (response: any) => {
        this.elements = response;
        if(this.defaultValue) {
          this.setDefaultValue(this.defaultValue);
        } else {
          // Iniciar countdown si no hay valor por defecto
          this.startCountdown();
        }
      },
    });
  }

  ngAfterViewInit(): void {
    this.viewInitialized.set(true);
    this.applyInitialStyles();

    if(this.defaultValue) {
      this.valueState.emit(this.defaultValue);
    }
  }

  private applyInitialStyles() {
    this.getStyleEnabled();
  }

  private safeApplyStyles(callback: () => void) {
    if (this.viewInitialized()) {
      callback();
    }
  }

  // Métodos para el control de timeout y estilos
  startCountdown() {
    this.clearCountdown();
    
    this.countdownTimer = setTimeout(() => {
      
      // Activar error si NO hay nada seleccionado
      if (!this.hasSelected() || !this.form.valid) {
        this.offTimeOut.set(true);
        this.getStyleError();
      }
    }, 8000); // 8 segundos
  }

  clearCountdown() {
    if (this.countdownTimer) {
      clearTimeout(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  getStyleError(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.addClass(this.inbox.nativeElement, 'border-error')
      }
    });
  }
  
  getStyleEnabled(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.addClass(this.inbox.nativeElement, 'border-enabled')
      }
    });
  }

  getStyleTextDisabled(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.addClass(this.inbox.nativeElement, 'border-disabled')
      }
    });
  }

  filter(event: any) {
    this.isValid = false;
    this.hasSelected.set(false); // Al filtrar, se resetea la selección
    
    const query = event.target.value.toLowerCase();

    this.filtered = this.elements.filter(item => {
      const nameMatch = item.label?.toLowerCase().includes(query);
      return nameMatch;
    });

    if (this.filtered.length === 0 && query.length > 0) {
      this.filtered = [{ value: 1120, label: 'OTRA' }];
    }
  }

  select(value: number, label: string) {
    this.form.get('element')!.setValue(label);
    this.isValid = true;
    this.filtered = [];
    this.valueState.emit(value);
    
    // Marcar como seleccionado y limpiar timeout
    this.hasSelected.set(true);
    this.clearCountdown();
    this.offTimeOut.set(false);
    this.getStyleEnabled();
  }

  esc() {
    this.filtered = [];
    this.isValid = false;
    this.form.get('element')!.reset();
    this.valueState.emit(null);
    this.hasSelected.set(false);
    
    // Reiniciar el countdown cuando se limpia
    this.startCountdown();
  }

  makeInvalid() {
    this.isValid = false;
    this.valueState.emit(null);
    this.hasSelected.set(false);
  }

  setDefaultValue(defaultValue: string) {
    this.form.get('element')!.setValue(defaultValue);
    this.isValid = true;
    this.hasSelected.set(true);
    
    const objs = this.elements.filter(element => element.value == defaultValue)[0];
    if(objs){
      this.element.nativeElement.value = objs.label;
    }
    
    // Limpiar timeout si hay valor por defecto
    this.clearCountdown();
    this.offTimeOut.set(false);
    this.getStyleEnabled();
  }

  validateBlur() {
    setTimeout( () => {
      if(!this.isValid) {
        this.esc();
      }
    }, 1000);
  }

  hasError = () => {
    const shouldShowError = this.offTimeOut() && (!this.hasSelected() || !this.form.valid);
    return shouldShowError;
  }

  ngOnDestroy() {
    this.clearCountdown();
  }
}